import React, { useCallback, useMemo } from "react";
import { Draggable } from "react-beautiful-dnd";
import { ClockCircleOutlined, EnvironmentOutlined, FieldTimeOutlined, FireFilled, HistoryOutlined, ExclamationOutlined } from "@ant-design/icons";
import { TicketWrapper, Header, Title, SubTitle, Info, DaysPast, HeaderLeftContainer, HeaderRightContainer, InfoText, ReportDateWrapper, ReportDateMainContainer, UpdateAndDeleteReportLabel, ReportDateContainer, ShipWrapper, StyledSubTitle, AssigneeName, InfoTextContainer, StyledExclamationIcon, StyledMoreTitle, StyledSpan, StatusPill, LfLocationBadge, RightIconsWrapper, PaidAmountBadge, OverdueBadge } from "./styles";
import { Ticket as TicketType } from "../../../../../types";
import { useNavigate } from "react-router-dom";
import SystemPayRoutes from "../../../../../utils/constants/Routes";
import { ReactComponent as Camera } from "../../../../../assets/images/Camera.svg";
import { ReactComponent as EmailUnreadIcon } from "../../../../../assets/images/emailUnread.svg";
import { ReactComponent as PayUsdIcon } from "../../../../../assets/images/payUsd.svg";
import { ReactComponent as SosIcon } from "../../../../../assets/images/SosIcon.svg";
import { ReactComponent as LuggageIcon } from "../../../../../assets/images/travel_luggage_and_bags.svg";
import { ReactComponent as UserRound } from "../../../../../assets/images/UserRound.svg";
import { ReactComponent as DeleteIcon } from "../../../../../assets/images/delete.svg";
import CountryFlag from "../../../../../components/CountryFlag";
import { ReactComponent as ShipDeliveredIcon } from "../../../../../assets/images/ShipDelivered.svg";
import { PlaneTakeoff, PlaneLanding, Plane } from "lucide-react";
import { useAppSelector } from "../../../../../utils/hooks/redux";
import { LostStatuses, PendingRequestOperation } from "../../../../../types/LostStatuses";
import { convertUtcToLocal } from "../../../../../utils/helpers/convertUtcToLocal";
import { ReportType } from "../../../../../redux/features/board/columnTypes";
import { ReactComponent as ShipIcon } from "../../../../../assets/images/boat_icon.svg";
import { capitalizeFirstLetter } from "../../../../../utils/helpers/capitalizeFirstLetter";
import { FoundItemTypeOptions, FoundStatuses } from "../../../../../types/FoundItemTypes";
import { Tooltip } from "antd";
import { checkHasPermission } from "../../../../../utils/helpers/checkHasPermission";
import { selectUserRole } from "../../../../../redux/features/auth/selectors";
import { permissions } from "../../../../../utils/constants/permissions";
import dayjs from "dayjs";
import { getStatusTheme } from "../../statusThemes";

interface TicketProps {
  ticket: TicketType;
  columnStatus?: number;
  index: number;
  forceFlatView?: boolean;
  showStatusPill?: boolean;
  statusLabel?: string;
}

const Ticket: React.FC<TicketProps> = ({ ticket, index, columnStatus, forceFlatView = false, showStatusPill = false, statusLabel }) => {
  const navigate = useNavigate();
  const { id, lostDate, lostTime, firstName, lastName, trackingId, status, createdAt, isUrgent, isOverdue: isSubscriptionOverdue, foundBy, foundDate, isPendingRequest, requestType, itemNumber, isFoundItem, location, ship, caseManagerName, foundType, shippingOptions, establishmentTypeIcon, establishmentType, countryCode, lfNotificationAnswer, lfNotificationCode, estAnswers, paidAmount, paidCurrency, planName, paymentDate, reviewedAt, lfNotifiedAt, deletedAt, isRefunded, refundedAt } = ticket;
  const openedColumn = useAppSelector((state) => state.columns.openedColumn); // Assuming this is in Redux state
  const openedColumnType = useAppSelector((state) => state.columns.openedColumnType);
  const boardStatusFilter = useAppSelector((state) => state.columns.boardStatusFilter);
  const userRole = useAppSelector(selectUserRole);
  const { data: paymentConfigurationData } = useAppSelector((state) => state.paymentConfiguration);
  // Fresh-payment highlight window comes from scheduler config (configurable in Settings).
  // When 0 (or unset) the highlight is disabled entirely.
  const recentPaymentHighlightHours = useAppSelector((state) => state.schedulerSettings.data.recentPaymentHighlightHours) ?? 26;
  const isRecentlyPaid = useMemo(() => {
    if (!paymentDate || !recentPaymentHighlightHours || recentPaymentHighlightHours <= 0) return false;
    const ageHours = (Date.now() - new Date(paymentDate).getTime()) / (1000 * 60 * 60);
    return ageHours >= 0 && ageHours <= recentPaymentHighlightHours;
  }, [paymentDate, recentPaymentHighlightHours]);

  // Refund happened within the highlight window — drives the same "hold off" visual treatment
  // (dashed border, reduced opacity) as isRecentlyPaid. Once paymentDate goes away after a
  // refund, isRecentlyPaid would otherwise stop firing, so refunded claims need their own check.
  const isRecentlyRefunded = useMemo(() => {
    if (!isRefunded || !refundedAt || !recentPaymentHighlightHours || recentPaymentHighlightHours <= 0) return false;
    const ageHours = (Date.now() - new Date(refundedAt).getTime()) / (1000 * 60 * 60);
    return ageHours >= 0 && ageHours <= recentPaymentHighlightHours;
  }, [isRefunded, refundedAt, recentPaymentHighlightHours]);

  // Hours/minutes between when the claim was reported and when the refund was requested.
  // Surfaced in the tooltip so admins can see how fast the customer reversed course.
  const timeFromReportToRefund = useMemo(() => {
    if (!isRefunded || !refundedAt || !createdAt) return "";
    const elapsedMs = new Date(refundedAt).getTime() - new Date(createdAt).getTime();
    if (elapsedMs < 0) return "";
    const totalMinutes = Math.floor(elapsedMs / 60000);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }, [isRefunded, refundedAt, createdAt]);

  const currentDate = new Date();
  const filedDateObj = new Date(createdAt);
  const daysPast = Math.floor((currentDate.getTime() - filedDateObj.getTime()) / (1000 * 60 * 60 * 24));
  const isOverdue = daysPast > 90;



  const userName = useMemo(() => {
    if (isFoundItem || foundBy) {
      return `${foundBy}`;
    }

    return `${firstName || ""} ${lastName || ""}`;
  }, [firstName, lastName, foundBy, isFoundItem]);

  const lostOrFoundDate = useMemo(() => {
    if (isFoundItem || foundDate) {
      return foundDate;
    }

    return lostDate;
  }, [lostDate, foundDate, columnStatus, isFoundItem]);

  const ticketNumber = useMemo(() => {
    if (isFoundItem) {
      return itemNumber;
    }
    return trackingId;
  }, [isFoundItem, trackingId])

  const handleOnClick = () => {
    // Check if we're viewing deleted items (either in opened column or board status filter)
    const isViewingDeleted = (openedColumn === LostStatuses.Deleted && openedColumnType === ReportType.Lost) ||
                             boardStatusFilter === LostStatuses.Deleted;
    const isViewingDiscarded = openedColumn === FoundStatuses.Discarded && openedColumnType === ReportType.Found;

    if (isViewingDiscarded) {
      navigate(`${SystemPayRoutes.foundReportBase}/${ticketNumber}?withDeleted=true`);
    } else if (isFoundItem) {
      navigate(`${SystemPayRoutes.foundReportBase}/${ticketNumber}`);
    } else if (isViewingDeleted) {
      navigate(`${SystemPayRoutes.reportBase}/${ticketNumber}?withDeleted=true`);
    } else {
      navigate(`${SystemPayRoutes.reportBase}/${ticketNumber}`)
    }
  }

  const getDaysPastStyles = (daysPast: number) => {
    if (daysPast > 60) {
      return { backgroundColor: "#FFADD2", border: "1px solid #F759AB", color: "black" };
    }
    if (daysPast > 30) {
      return { backgroundColor: "#FFD6E7", border: "1px solid #FF85C0", color: "black" };
    }
    return { backgroundColor: "#FFF0F6", border: "1px solid #FFADD2", color: "black" };
  };

  const isUpdatePendingRequest = useMemo(() => {
    return isPendingRequest && (requestType === PendingRequestOperation.Update || requestType === PendingRequestOperation.UpdateShippingInfo);
  }, [requestType, isPendingRequest]);

  const hasPermission = useCallback(
    (permissionKey: string) => checkHasPermission(userRole, permissionKey),
    [userRole]
  );

  const isAccessPaymentLogs = useMemo(() => hasPermission(permissions.IsPaymentLogAllowed), [hasPermission]);
  const isPaymentSuccess = useMemo(() => Boolean(ticket?.isPaymentSuccess), [ticket]);
  const showPaymentPendingIcon = useMemo(() => {
    if (!isAccessPaymentLogs) return false;
    const isShippingRequired = Array.isArray(shippingOptions) && shippingOptions.length > 0;
    return isShippingRequired && !isPaymentSuccess;
  }, [isAccessPaymentLogs, shippingOptions, isPaymentSuccess]);

  const isPayAfterCutOffDateOverdue = useMemo(() => {
    if (!paymentConfigurationData || !shippingOptions?.length || isPaymentSuccess) return false;
    const { paymentDueDays } = paymentConfigurationData;
    return shippingOptions?.length > 0 && shippingOptions.every(item => {
      const deadline = dayjs(item.cutOffDate).add(paymentDueDays, "day");
      return dayjs().isAfter(deadline);
    });
  }, [shippingOptions, paymentConfigurationData, ticket, isPaymentSuccess]);

  const categoriesTooltip = ticket?.items
    ?.map((item) => `${item.categoryName || "Other Category"} - ${item.subCategoryName || ""}`)
    .join("\n");

  const firstCategory = ticket?.items?.[0]?.categoryName || "Other Category";
  const firstSubCategory = ticket?.items?.[0]?.subCategoryName || "";

  const remainingCount = (ticket?.items?.length || 0) - 1;
  const currencySymbol = paidCurrency === 'EUR' ? '€' : paidCurrency === 'GBP' ? '£' : '$';

  // Human-readable remaining time within the REFUND eligibility window. This is a fixed
  // business rule (24h) — separate from the configurable "recent payment highlight" setting
  // which only drives the visual styling. Returns "" once the window has lapsed.
  const REFUND_WINDOW_HOURS = 24;
  const refundWindowRemaining = useMemo(() => {
    if (!paymentDate) return "";
    const remainingMs = REFUND_WINDOW_HOURS * 60 * 60 * 1000 - (Date.now() - new Date(paymentDate).getTime());
    if (remainingMs <= 0) return "";
    const totalMinutes = Math.floor(remainingMs / 60000);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return h > 0 ? `${h}h ${m}m left to refund` : `${m}m left to refund`;
  }, [paymentDate]);

  const paidTooltip = useMemo(() => {
    if (!paidAmount) return "";
    const parts: string[] = [];
    if (planName) parts.push(`Plan: ${planName}`);
    if (paymentDate) parts.push(`Paid: ${convertUtcToLocal(paymentDate, "MM/DD/YYYY")}`);
    if (paidCurrency && paidCurrency !== 'USD') parts.push(`Currency: ${paidCurrency}`);
    if (refundWindowRemaining) parts.push(`⏱ ${refundWindowRemaining}`);
    return parts.join("\n");
  }, [paidAmount, planName, paymentDate, paidCurrency, refundWindowRemaining]);

  const statusTheme = getStatusTheme(status);
  const shouldUseFlatLayout = openedColumn || forceFlatView;
  const computedStatusLabel = statusLabel || statusTheme.label;

  // Hover hint surfaced on cards inside the highlight window (configurable in Settings) —
  // tells admins to skip and work on the actionable (past-window) cards above instead. Two
  // variants:
  //   • Refund-requested: customer already triggered a refund; admin should not progress.
  //   • Recently-paid: still within the FIXED 24h refund window, refund still possible.
  const refundHoldTooltip = useMemo(() => {
    if (isRecentlyRefunded) {
      return (
        <div style={{ fontSize: 12, lineHeight: 1.45 }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>🔄 Refund requested — do not work on this</div>
          {timeFromReportToRefund && (
            <div>Customer requested a refund <strong>{timeFromReportToRefund}</strong> after filing.</div>
          )}
          <div style={{ marginTop: 4, fontSize: 11, color: "#bfbfbf" }}>
            Payment returned — any work here is wasted effort.
          </div>
        </div>
      );
    }
    if (!isRecentlyPaid) return null;
    return (
      <div style={{ fontSize: 12, lineHeight: 1.45 }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>🔒 Recently paid — hold off</div>
        {refundWindowRemaining ? (
          <>
            <div>Customer can still request a refund for the next <strong>{refundWindowRemaining}</strong>.</div>
          </>
        ) : (
          <div>Refund window closed, but this is still a fresh payment.</div>
        )}
        <div style={{ marginTop: 4, fontSize: 11, color: "#bfbfbf" }}>
          Focus on past-window claims first.
        </div>
      </div>
    );
  }, [isRecentlyPaid, isRecentlyRefunded, refundWindowRemaining, timeFromReportToRefund]);

  // Helper: always show max 3 chars, uppercased
  const formatBadgeCode = (raw: string) => (raw || "").toUpperCase().slice(0, 3);

  // L&F Location display: show code first if available, otherwise truncated name. Full text on hover.
  const lfLocationDisplay = useMemo(() => {
    if (!lfNotificationAnswer) return null;
    const rawDisplay = lfNotificationCode || lfNotificationAnswer;
    const displayText = formatBadgeCode(rawDisplay);
    const fullText = `Lost Airport: ${lfNotificationCode ? `${lfNotificationCode} - ${lfNotificationAnswer}` : lfNotificationAnswer}`;
    return { displayText, fullText };
  }, [lfNotificationAnswer, lfNotificationCode]);

  // Establishment answer badges (Departure Airport, Arrival Airport, Airline, etc.)
  // Order: Departure, Arrival, Airline (Lost is rendered separately via lfLocationDisplay)
  const estBadges = useMemo(() => {
    if (!estAnswers || typeof estAnswers !== "object") return [];
    type Kind = "departure" | "arrival" | "airline";
    const kindOrder: Kind[] = ["departure", "arrival", "airline"];
    const kindColors: Record<Kind, { bg: string; fg: string; border: string }> = {
      departure: { bg: "#fef3c7", fg: "#92400e", border: "#fde68a" },
      arrival:   { bg: "#e0f2fe", fg: "#075985", border: "#bae6fd" },
      airline:   { bg: "#ede9fe", fg: "#5b21b6", border: "#ddd6fe" },
    };
    const byKind: Partial<Record<Kind, { icon: React.ReactNode; code: string; tooltip: string; key: string; colors: { bg: string; fg: string; border: string } }>> = {};
    for (const [fieldName, value] of Object.entries(estAnswers)) {
      if (!value?.code && !value?.name) continue;
      const f = fieldName.toLowerCase();
      const code = formatBadgeCode(value.code || value.name || "");
      const tooltip = `${fieldName}: ${value.code ? `${value.code} - ${value.name}` : value.name}`;
      let kind: Kind | null = null;
      let icon: React.ReactNode = null;
      if (f.includes("departure")) { kind = "departure"; icon = <PlaneTakeoff size={9} />; }
      else if (f.includes("arrival")) { kind = "arrival"; icon = <PlaneLanding size={9} />; }
      else if (f.includes("airline")) { kind = "airline"; icon = <Plane size={9} />; }
      if (!kind) continue;
      if (!byKind[kind]) byKind[kind] = { icon, code, tooltip, key: fieldName, colors: kindColors[kind] };
    }
    return kindOrder.map(k => byKind[k]).filter((b): b is NonNullable<typeof b> => !!b);
  }, [estAnswers]);

  // Tooltip content with all relevant dates based on status
  const datesTooltip = useMemo(() => {
    const capitalize = (s?: string) => s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
    const lostLine = (
      <div><strong>Lost: {lostDate ? convertUtcToLocal(lostDate, "MM/DD/YYYY") : "-"}{lostTime ? ` – ${capitalize(lostTime)}` : ""}</strong></div>
    );
    const reportedLine = createdAt && <div>Reported: {convertUtcToLocal(createdAt, "MM/DD/YYYY hh:mm A")}</div>;
    const paidLine = paymentDate && <div>Paid: {convertUtcToLocal(paymentDate, "MM/DD/YYYY hh:mm A")}</div>;
    const reviewLine = reviewedAt
      ? <div>Review: {convertUtcToLocal(reviewedAt, "MM/DD/YYYY hh:mm A")}</div>
      : null;
    const lfNotifiedLine = (status === LostStatuses.LFNotified || lfNotifiedAt) && lfNotifiedAt
      ? <div>LF Notified: {convertUtcToLocal(lfNotifiedAt, "MM/DD/YYYY hh:mm A")}</div>
      : null;
    const deletedLine = status === LostStatuses.Deleted && deletedAt
      ? <div>Deleted: {convertUtcToLocal(deletedAt, "MM/DD/YYYY hh:mm A")}</div>
      : null;

    return (
      <div style={{ fontSize: 12, lineHeight: 1.6 }}>
        {lostLine}
        {reportedLine}
        {paidLine}
        {status === LostStatuses.Review && reviewLine}
        {status === LostStatuses.LFNotified && (<>{reviewLine}{lfNotifiedLine}</>)}
        {status === LostStatuses.Deleted && (<>{reviewLine}{lfNotifiedLine}{deletedLine}</>)}
      </div>
    );
  }, [lostDate, lostTime, createdAt, paymentDate, reviewedAt, lfNotifiedAt, deletedAt, status]);

  return (
    <>
      {shouldUseFlatLayout ? (
        <Tooltip title={(isRecentlyPaid || isRecentlyRefunded) ? refundHoldTooltip : undefined} placement="top" mouseEnterDelay={0.25}>
        <TicketWrapper
          status={status}
          isUrgent={isUrgent}
          isPendingRequest={isPendingRequest}
          isPaymentSuccess={isPaymentSuccess}
          isRecentlyPaid={isRecentlyPaid || isRecentlyRefunded}
          requestType={requestType}
          onClick={() => handleOnClick()}>
          <Header>
            <HeaderLeftContainer>
              {isUrgent && <FireFilled style={{ color: "#FF85C0" }} />}
              {ticket?.documents > 0 && <Camera />}
              {!isFoundItem && daysPast > 0 && (
                <DaysPast style={getDaysPastStyles(daysPast)} isOverdue={isOverdue} isOnTime={!isOverdue}>
                  <ClockCircleOutlined style={{ fontSize: 10, color: "#000" }} />{`${daysPast}`}
                </DaysPast>
              )}
              {ticketNumber}
            </HeaderLeftContainer>
            <HeaderRightContainer>
              {ticket?.isEmailCommunication && <EmailUnreadIcon />}
              {ticket?.isSticky && <SosIcon />}
              {isFoundItem && foundType === FoundItemTypeOptions.FoundLuggage && <LuggageIcon />}
              {isPendingRequest && (
                <UpdateAndDeleteReportLabel bgColor={isUpdatePendingRequest ? "#FAAD14" : "#F5222D"}>
                  {isUpdatePendingRequest ? <HistoryOutlined /> : <DeleteIcon />}
                </UpdateAndDeleteReportLabel>
              )}
              {showPaymentPendingIcon && <PayUsdIcon />}
              {isPayAfterCutOffDateOverdue && <StyledExclamationIcon size={12} />}
              {showStatusPill && computedStatusLabel && <StatusPill $textColor={statusTheme.pillColor} $backgroundColor={statusTheme.pillBackground}>{computedStatusLabel}</StatusPill>}
            </HeaderRightContainer>
          </Header>
          <Tooltip placement="topLeft" title={<span style={{ whiteSpace: "pre-line" }}>{categoriesTooltip}</span>}>
            <Title style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <StyledSpan>
                {firstCategory}
                {firstSubCategory && <StyledSubTitle> - {firstSubCategory}</StyledSubTitle>}
              </StyledSpan>
              {remainingCount > 0 && (<StyledMoreTitle>+{remainingCount} More</StyledMoreTitle>)}
            </Title>
          </Tooltip>
          <SubTitle>
            <InfoTextContainer> {isFoundItem ? <EnvironmentOutlined color="#000000" /> : <CountryFlag countryCode={countryCode} />} <InfoText>{isFoundItem ? location : (userName || "Unknown")}</InfoText></InfoTextContainer>
            {paidAmount != null && (
              <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{paidTooltip}</span>}>
                <PaidAmountBadge>{currencySymbol}{Math.round(Number(paidAmount))}</PaidAmountBadge>
              </Tooltip>
            )}
            {isSubscriptionOverdue && (
              <Tooltip title="Subscription expired — claim still active in its current status">
                <OverdueBadge>Overdue</OverdueBadge>
              </Tooltip>
            )}
            {caseManagerName && <AssigneeName>{capitalizeFirstLetter(caseManagerName)}</AssigneeName>}
          </SubTitle>
          <ReportDateMainContainer>
            <ReportDateWrapper style={{ width: "100%" }}>
              <Info>{isFoundItem ? "Found" : "Lost"}:</Info>
              <Tooltip title={isFoundItem ? undefined : datesTooltip}>
                <InfoText style={{ cursor: isFoundItem ? "default" : "help" }}>{lostOrFoundDate && convertUtcToLocal(lostOrFoundDate, "MM/DD/YY")}</InfoText>
              </Tooltip>
              {paymentDate && (
                <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5 }}>
                  {/* Muted while inside the refund window — admin should skip these. Past-window
                      cards render with the normal Paid label color so they read as actionable. */}
                  <Info style={isRecentlyPaid ? { color: "#8c8c8c" } : undefined}>
                    {isRecentlyPaid ? "🔒 Paid:" : "Paid:"}
                  </Info>
                  <Tooltip title={paidTooltip || undefined}>
                    <InfoText
                      style={{
                        cursor: paidTooltip ? "help" : "default",
                        ...(isRecentlyPaid ? { color: "#8c8c8c" } : null),
                      }}
                    >{convertUtcToLocal(paymentDate, "MM/DD/YY hh:mm A")}</InfoText>
                  </Tooltip>
                </span>
              )}
            </ReportDateWrapper>
            {ship && <ShipWrapper>
              {<ShipIcon />} <InfoText>{ship}</InfoText>
            </ShipWrapper>}
            <RightIconsWrapper style={{ width: "100%", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                {lfLocationDisplay && (
                  <>
                    <Tooltip title={lfLocationDisplay.fullText}>
                      <LfLocationBadge>
                        {lfLocationDisplay.displayText}
                      </LfLocationBadge>
                    </Tooltip>
                    {estBadges.length > 0 && (
                      <span style={{ width: 1, height: 14, background: "#d1d5db", margin: "0 2px" }} />
                    )}
                  </>
                )}
                {estBadges.map(b => (
                  <Tooltip key={b.key} title={b.tooltip}>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 2,
                      padding: "1px 5px", borderRadius: 8, fontSize: 10, fontWeight: 600,
                      background: b.colors.bg, color: b.colors.fg, border: `1px solid ${b.colors.border}`,
                      lineHeight: 1.4
                    }}>
                      {b.icon}{b.code}
                    </span>
                  </Tooltip>
                ))}
              </div>
              {establishmentTypeIcon && <Tooltip title={establishmentType}><img src={establishmentTypeIcon} alt={'icon'} height={16} width={16} /></Tooltip>}
            </RightIconsWrapper>
          </ReportDateMainContainer>
        </TicketWrapper>
        </Tooltip>
      ) : (
        <Draggable isDragDisabled={true} draggableId={(columnStatus === LostStatuses.Urgent ? `${status}-${id}` : id) || `ticket-${index}`} index={index}>
          {(provided) => (
            <Tooltip title={(isRecentlyPaid || isRecentlyRefunded) ? refundHoldTooltip : undefined} placement="top" mouseEnterDelay={0.25}>
            <TicketWrapper
              status={status}
              isUrgent={isUrgent}
              isPendingRequest={isPendingRequest}
              requestType={requestType}
              isPaymentSuccess={isPaymentSuccess}
              isRecentlyPaid={isRecentlyPaid || isRecentlyRefunded}
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              onClick={() => handleOnClick()}
            >
              <Header>
                <HeaderLeftContainer>
                  {ticketNumber}
                  {isUrgent && <FireFilled style={{ color: "#FF85C0" }} />}
                  {ticket?.documents > 0 && <Camera />}
                  {!isFoundItem && daysPast > 0 && (
                    <DaysPast style={getDaysPastStyles(daysPast)} isOverdue={isOverdue} isOnTime={!isOverdue}>
                      <ClockCircleOutlined style={{ fontSize: 12, color: "#000" }} />{`${daysPast}`}
                    </DaysPast>
                  )}
                </HeaderLeftContainer>
                <HeaderRightContainer>
                  {ticket?.isEmailCommunication && <EmailUnreadIcon />}
                  {ticket?.isSticky && <SosIcon />}
                  {isFoundItem && foundType === FoundItemTypeOptions.FoundLuggage && <LuggageIcon />}
                  {isPendingRequest && (
                    <UpdateAndDeleteReportLabel bgColor={isUpdatePendingRequest ? "#FAAD14" : "#F5222D"}>
                      {isUpdatePendingRequest ? <HistoryOutlined /> : <DeleteIcon />}
                    </UpdateAndDeleteReportLabel>
                  )}
                  {showPaymentPendingIcon && <PayUsdIcon />}
                  {isPayAfterCutOffDateOverdue && <StyledExclamationIcon />}
                  {showStatusPill && computedStatusLabel && <StatusPill $textColor={statusTheme.pillColor} $backgroundColor={statusTheme.pillBackground}>{computedStatusLabel}</StatusPill>}
                </HeaderRightContainer>
              </Header>
              <Tooltip placement="topLeft" title={<span style={{ whiteSpace: "pre-line" }}>{categoriesTooltip}</span>}>
                <Title style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <StyledSpan>
                    {firstCategory}
                    {firstSubCategory && <StyledSubTitle> - {firstSubCategory}</StyledSubTitle>}
                  </StyledSpan>
                  {remainingCount > 0 && (<StyledMoreTitle>+{remainingCount} More</StyledMoreTitle>)}
                </Title>
              </Tooltip>
              <SubTitle>
                <InfoTextContainer>{isFoundItem ? <EnvironmentOutlined color="#000000" /> : <CountryFlag countryCode={countryCode} />} <InfoText>{isFoundItem ? location : (userName || "Unknown")}</InfoText></InfoTextContainer>
                {paidAmount != null && (
                  <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{paidTooltip}</span>}>
                    <PaidAmountBadge>{currencySymbol}{Math.round(Number(paidAmount))}</PaidAmountBadge>
                  </Tooltip>
                )}
                {isSubscriptionOverdue && (
                  <Tooltip title="Subscription expired — claim still active in its current status">
                    <OverdueBadge>Overdue</OverdueBadge>
                  </Tooltip>
                )}
                {caseManagerName && <AssigneeName>{capitalizeFirstLetter(caseManagerName)}</AssigneeName>}
              </SubTitle>
              <ReportDateMainContainer>
                <ReportDateContainer style={{ width: "100%" }}>
                  <ReportDateWrapper style={{ width: "100%" }}>
                    <Info>{isFoundItem ? "Found" : "Lost"}:</Info>
                    <InfoText>{lostOrFoundDate && convertUtcToLocal(lostOrFoundDate, "MM/DD/YY")}</InfoText>
                    {paymentDate && (
                      <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5 }}>
                        {/* Muted while inside the highlight window — admin should skip these.
                            Past-window cards render with the normal Paid label color. */}
                        <Info style={isRecentlyPaid ? { color: "#8c8c8c" } : undefined}>
                          {isRecentlyPaid ? "🔒 Paid:" : "Paid:"}
                        </Info>
                        <Tooltip title={paidTooltip || undefined}>
                          <InfoText
                            style={{
                              cursor: paidTooltip ? "help" : "default",
                              ...(isRecentlyPaid ? { color: "#8c8c8c" } : null),
                            }}
                          >{convertUtcToLocal(paymentDate, "MM/DD/YY hh:mm A")}</InfoText>
                        </Tooltip>
                      </span>
                    )}
                  </ReportDateWrapper>
                </ReportDateContainer>
                {ship && <ShipWrapper>
                  {<ShipIcon />} <InfoText>{ship}</InfoText>
                </ShipWrapper>}
                <RightIconsWrapper style={{ width: "100%", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                    {lfLocationDisplay && (
                      <Tooltip title={lfLocationDisplay.fullText}>
                        <LfLocationBadge>
                          {lfLocationDisplay.displayText}
                        </LfLocationBadge>
                      </Tooltip>
                    )}
                    {estBadges.map(b => (
                      <Tooltip key={b.key} title={b.tooltip}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 2,
                          padding: "1px 5px", borderRadius: 8, fontSize: 10, fontWeight: 600,
                          background: b.colors.bg, color: b.colors.fg, border: `1px solid ${b.colors.border}`,
                          lineHeight: 1.4
                        }}>
                          {b.icon}{b.code}
                        </span>
                      </Tooltip>
                    ))}
                  </div>
                  {establishmentTypeIcon && <Tooltip title={establishmentType}><img src={establishmentTypeIcon} alt={'icon'} height={16} width={16} /></Tooltip>}
                </RightIconsWrapper>
              </ReportDateMainContainer>
            </TicketWrapper>
            </Tooltip>
          )}
        </Draggable>
      )}
    </>
  );
};

export default Ticket;
